/**
 * Designer Header Component
 * 
 * Unified header component for both the designs list and designer pages,
 * with Workplace Home, My Designs buttons, centered Biamp logo and title, and user info
 */

import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../atoms/ThemeToggle';
import RdButton from '../atoms/RdButton';

// Styled Components
const Header = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '15px 40px',
  borderBottom: `0.6px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const HeaderCenter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
}));

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '10px',
  flex: 1,
  justifyContent: 'flex-end',
}));

interface DesignerHeaderProps {
  showMyDesigns?: boolean;
  projectName?: string;
}

const DesignerHeader: React.FC<DesignerHeaderProps> = ({ showMyDesigns = true, projectName }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, signout } = useAuth();
  
  // Fallback to sessionStorage if auth context doesn't have user
  const selectedOrgName = sessionStorage.getItem('selectedOrgName') || '';
  
  const handleWorkplaceHome = () => {
    const workplaceHomeUrl = process.env.REACT_APP_WORKPLACE_HOME_URL || 'https://stage.workplace.biamp.app';
    const orgDomain = sessionStorage.getItem('selectedOrgDomain');
    const path = orgDomain ? `/admin/${orgDomain}` : '/admin/';
    const url = new URL(path, workplaceHomeUrl);

    if (user?.profile?.email) {
      url.searchParams.set('login_hint', user.profile.email);
    }

    const orgId = sessionStorage.getItem('selectedOrgId');
    if (orgId) url.searchParams.set('org_id', orgId);

    const membershipId = sessionStorage.getItem('selectedMembershipId');
    if (membershipId) url.searchParams.set('membership_id', membershipId);

    const region = sessionStorage.getItem('selectedRegion');
    if (region) url.searchParams.set('region', region);

    window.location.href = url.toString();
  };
  
  const handleMyDesigns = () => {
    navigate('/designs');
  };

  const handleSignout = async () => {
    try {
      // Call the sign out function from AuthContext
      await signout();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  // Left chevron icon component that respects theme
  const LeftChevronIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 12L6 8L10 4" stroke={theme.palette.text.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // MyDesigns icon component that respects theme
  const MyDesignsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M3.86661 4.00001C3.86661 3.55818 4.22478 3.20001 4.66661 3.20001L13.6666 3.20001C14.1084 3.20001 14.4666 3.55818 14.4666 4.00001C14.4666 4.44184 14.1084 4.80001 13.6666 4.80001L4.66661 4.80001C4.22478 4.80001 3.86661 4.44184 3.86661 4.00001Z" fill={theme.palette.text.primary}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M3.86661 8.00001C3.86661 7.55818 4.22478 7.20001 4.66661 7.20001L13.6666 7.20001C14.1084 7.20001 14.4666 7.55818 14.4666 8.00001C14.4666 8.44184 14.1084 8.80001 13.6666 8.80001L4.66661 8.80001C4.22478 8.80001 3.86661 8.44184 3.86661 8.00001Z" fill={theme.palette.text.primary}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M3.86661 12C3.86661 11.5582 4.22478 11.2 4.66661 11.2L13.6666 11.2C14.1084 11.2 14.4666 11.5582 14.4666 12C14.4666 12.4418 14.1084 12.8 13.6666 12.8L4.66661 12.8C4.22478 12.8 3.86661 12.4418 3.86661 12Z" fill={theme.palette.text.primary}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M1.99995 3.20001C2.44178 3.20001 2.79995 3.55818 2.79995 4.00001V4.00668C2.79995 4.44851 2.44178 4.80668 1.99995 4.80668C1.55812 4.80668 1.19995 4.44851 1.19995 4.00668V4.00001C1.19995 3.55818 1.55812 3.20001 1.99995 3.20001Z" fill={theme.palette.text.primary}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M1.99995 7.20001C2.44178 7.20001 2.79995 7.55818 2.79995 8.00001V8.00668C2.79995 8.44851 2.44178 8.80668 1.99995 8.80668C1.55812 8.80668 1.19995 8.44851 1.19995 8.00668V8.00001C1.19995 7.55818 1.55812 7.20001 1.99995 7.20001Z" fill={theme.palette.text.primary}/>
      <path fillRule="evenodd" clipRule="evenodd" d="M1.99995 11.2C2.44178 11.2 2.79995 11.5582 2.79995 12V12.0067C2.79995 12.4485 2.44178 12.8067 1.99995 12.8067C1.55812 12.8067 1.19995 12.4485 1.19995 12.0067V12C1.19995 11.5582 1.55812 11.2 1.99995 11.2Z" fill={theme.palette.text.primary}/>
    </svg>
  );

  return (
    <Header id="designerHeader">
      {/* Left side - Navigation buttons */}
      <HeaderLeft>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <RdButton
            id="backToWorkplaceButton"
            data-testid="backToWorkplaceButton"
            onClick={handleWorkplaceHome}
            startIcon={<LeftChevronIcon />}
            sx={{ '& .MuiButton-startIcon': { marginRight: '0px' } }}
          >
            Back to Workplace
          </RdButton>
          {showMyDesigns && (
            <RdButton 
              id="myDesignsButton" 
              onClick={handleMyDesigns}
              startIcon={<MyDesignsIcon />}
            >
              My designs
            </RdButton>
          )}
        </Box>
      </HeaderLeft>

      {/* Center - Biamp logo and Workplace Designer title */}
      <HeaderCenter>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
            <rect x="0.5" width="24" height="24" rx="4" fill="#D22730"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M12.0515 17.746C14.403 17.746 16.2888 15.8059 16.2888 13.2944V13.2973C16.2888 10.8229 14.403 8.86276 12.0515 8.86276C11.0886 8.86276 10.2885 9.1142 9.62849 9.53993V4.92828L7.15408 6.33407V17.5689H9.62849V17.0689C10.2885 17.4975 11.0914 17.746 12.0515 17.746ZM11.64 10.9657C12.9229 10.9657 13.8487 12.0515 13.8487 13.2973C13.8487 14.5602 12.9229 15.6659 11.64 15.6659C10.9114 15.6659 10.1799 15.4174 9.62849 14.6687V11.9458C10.1799 11.2143 10.9114 10.9657 11.64 10.9657Z" fill="white"/>
            <path d="M17.5431 17.746C18.1975 17.746 18.726 17.2174 18.726 16.5631C18.726 15.9088 18.1975 15.3802 17.5431 15.3802C16.8888 15.3802 16.3602 15.9088 16.3602 16.5631C16.3602 17.2174 16.8888 17.746 17.5431 17.746Z" fill="white"/>
            <path d="M6.24834 17.1003H6.21691V17.5632H6.25119V17.1603L6.43406 17.426L6.61692 17.1603V17.5632H6.65121V17.1003H6.61978L6.43406 17.3746L6.24834 17.1003Z" fill="white"/>
            <path d="M5.75403 17.1346H5.91404V17.5661H5.94832V17.1346H6.11119V17.1032H5.75403V17.1346Z" fill="white"/>
          </svg>
          <Typography 
            sx={{ 
              color: theme.palette.text.primary,
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '150%',
              letterSpacing: '-0.32px',
              fontFeatureSettings: "'liga' off, 'clig' off"
            }}
          >
            Workplace Designer
          </Typography>
        </Box>
      </HeaderCenter>

      {/* Right side - Organization, User info, Theme toggle and Sign out */}
      <HeaderRight>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: '200px',
            overflow: 'hidden',
          }}
        >
          <Typography
            component="span"
            sx={{
              color: theme.palette.text.primary,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: '"Open Sans"',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: 'normal',
              marginRight: '5px',
              flexShrink: 0,
            }}
          >
            Organization
          </Typography>
          <Tooltip
            title={selectedOrgName || 'None'}
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[2],
                  fontSize: '12px',
                  fontFamily: '"Open Sans"',
                },
              },
              arrow: {
                sx: {
                  color: theme.palette.background.paper,
                  '&::before': {
                    border: `1px solid ${theme.palette.divider}`,
                  },
                },
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                color: theme.palette.text.primary,
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontFamily: '"Open Sans"',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOrgName || 'None'}
            </Typography>
          </Tooltip>
        </Box>
      </HeaderRight>
    </Header>
  );
};

export default DesignerHeader;